import React, { useMemo } from 'react';
import { Zap } from 'lucide-react';
import { Card, DonutChart, getNodeData } from './primitives';
import { useTopology } from '../TopologyContext';

// ===== Cluster summary (top of dashboard, desktop) =====
export function ClusterSummaryCard({ resources, totalJobs }) {
  const { ALL_NODES, NODE_COUNT } = useTopology();
  const { cpu, cores, usedCores, mem, usedMem, online } = useMemo(() => {
    const nodes = getNodeData(resources, ALL_NODES);
    return {
      cpu: nodes.length ? nodes.reduce((s, n) => s + (n.cpu_usage || 0), 0) / nodes.length : 0,
      cores: nodes.reduce((s, n) => s + (n.cpu_cores || 0), 0),
      usedCores: nodes.reduce((s, n) => s + (n.used_cores || 0), 0),
      mem: nodes.reduce((s, n) => s + (n.total_mem || 0), 0),
      usedMem: nodes.reduce((s, n) => s + (n.used_mem || 0), 0),
      online: nodes.filter(r => !r.error).length,
    };
  }, [resources, ALL_NODES]);

  return (
    <Card style={{ gridRow: 'span 2' }}>
      <h3 style={{ color: '#9ca3af', fontSize: 14, marginBottom: 4 }}>Cluster Utilization</h3>
      <p style={{ color: '#4b5563', fontSize: 12, marginBottom: 24 }}>
        PowerFlow CFD — {NODE_COUNT}-Node Cluster
      </p>
      <DonutChart value={cpu} max={100} sublabel={`of ${cores} cores`} />
      <div style={{ marginTop: 32 }}>
        {[
          ['CPU Usage', `${usedCores.toFixed(0)}/${cores}`],
          ['Memory', `${usedMem.toFixed(0)}/${mem.toFixed(0)} GB`],
          ['Nodes Online', `${online}/${NODE_COUNT}`],
          ['Active Jobs', `${totalJobs.running} running`],
        ].map(([l, v], i, arr) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', padding: '12px 0',
            borderBottom: i < arr.length - 1 ? '1px solid #1e293b' : 'none',
          }}>
            <span style={{ color: '#9ca3af', fontSize: 14 }}>{l}</span>
            <span style={{ color: '#fff', fontSize: 14, fontWeight: 500 }}>{v}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ===== Mobile compact 4-stat strip =====
export function MobileSummaryCard({ resources, jobs }) {
  const { ALL_NODES, NODE_COUNT } = useTopology();
  const { cpu, cores, online } = useMemo(() => {
    const nodes = getNodeData(resources, ALL_NODES);
    return {
      cpu: nodes.length ? nodes.reduce((s, n) => s + (n.cpu_usage || 0), 0) / nodes.length : 0,
      cores: nodes.reduce((s, n) => s + (n.cpu_cores || 0), 0),
      online: nodes.filter(r => !r.error).length,
    };
  }, [resources, ALL_NODES]);

  return (
    <Card>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 8, textAlign: 'center',
      }}>
        <div>
          <p style={{ color: '#9ca3af', fontSize: 11, marginBottom: 4 }}>CPU</p>
          <p style={{
            color: cpu >= 90 ? '#ef4444' : '#22c55e',
            fontSize: 24, fontWeight: 'bold',
          }}>{cpu.toFixed(0)}%</p>
        </div>
        <div>
          <p style={{ color: '#9ca3af', fontSize: 11, marginBottom: 4 }}>Running</p>
          <p style={{ color: '#22c55e', fontSize: 24, fontWeight: 'bold' }}>
            {jobs.running.length}
          </p>
        </div>
        <div>
          <p style={{ color: '#9ca3af', fontSize: 11, marginBottom: 4 }}>Nodes</p>
          <p style={{ color: '#3b82f6', fontSize: 24, fontWeight: 'bold' }}>
            {online}/{NODE_COUNT}
          </p>
        </div>
        <div>
          <p style={{ color: '#9ca3af', fontSize: 11, marginBottom: 4 }}>Cores</p>
          <p style={{ color: '#fff', fontSize: 24, fontWeight: 'bold' }}>{cores}</p>
        </div>
      </div>
    </Card>
  );
}

// ===== Total compute banner (bottom of desktop dashboard) =====
export function TotalResourcesCard({ resources }) {
  const { ALL_NODES, NODE_COUNT } = useTopology();
  const { cores, mem, online } = useMemo(() => {
    const nodes = getNodeData(resources, ALL_NODES);
    return {
      cores: nodes.reduce((s, n) => s + (n.cpu_cores || 0), 0),
      mem: nodes.reduce((s, n) => s + (n.total_mem || 0), 0),
      online: nodes.filter(r => !r.error).length,
    };
  }, [resources, ALL_NODES]);

  return (
    <div style={{
      backgroundColor: '#0f172a', borderRadius: 20, padding: 24,
      border: '1px solid #334155',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: 'linear-gradient(135deg, #eab308, #f97316)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Zap style={{ width: 24, height: 24, color: '#fff' }} />
        </div>
        <div>
          <p style={{ color: '#9ca3af', fontSize: 12, marginBottom: 4 }}>
            Total Compute Power
          </p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 28, fontWeight: 'bold', color: '#fff' }}>{cores}</span>
            <span style={{ color: '#6b7280' }}>cores</span>
            <span style={{ fontSize: 28, fontWeight: 'bold', color: '#fff', marginLeft: 16 }}>
              {mem.toFixed(0)}
            </span>
            <span style={{ color: '#6b7280' }}>GB RAM</span>
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <p style={{ color: '#fff', fontSize: 20, fontWeight: 600 }}>
          {online}/{NODE_COUNT} Nodes
        </p>
        <p style={{ color: '#6b7280', fontSize: 14 }}>Online & Ready</p>
      </div>
    </div>
  );
}

// ===== Mini total card (mobile) =====
export function MiniTotalCard({ resources }) {
  const { ALL_NODES, NODE_COUNT } = useTopology();
  const { cores, mem, online } = useMemo(() => {
    const nodes = getNodeData(resources, ALL_NODES);
    return {
      cores: nodes.reduce((s, n) => s + (n.cpu_cores || 0), 0),
      mem: nodes.reduce((s, n) => s + (n.total_mem || 0), 0),
      online: nodes.filter(r => !r.error).length,
    };
  }, [resources, ALL_NODES]);

  return (
    <Card style={{ padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
        <Zap style={{ width: 14, height: 14, color: '#eab308' }} />
        <span style={{ color: '#9ca3af', fontSize: 11 }}>Total</span>
      </div>
      <p style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>{cores} cores</p>
      <p style={{ color: '#6b7280', fontSize: 11 }}>{mem.toFixed(0)}GB</p>
      <p style={{ color: '#22c55e', fontSize: 10, marginTop: 6 }}>{online}/{NODE_COUNT} online</p>
    </Card>
  );
}
